import { adCodeStrings } from 'constants/strings'
import type {
  TAdCodeAttribution,
  TAdCodeConfidence,
  TAdCodeMedium,
  TAdCodeMetrics,
  TAdCodeStatus,
} from 'types'

/** 計測できない値は「—」で表す（集計管理と揃える）。 */
export const EMPTY_VALUE = '—'

export const formatCount = (value: number): string => value.toLocaleString()

/** 登録率。分母が0のときは率を出さない。 */
export const formatRate = (rate: number | null): string =>
  rate === null ? EMPTY_VALUE : `${Math.round(rate * 1000) / 10}%`

/** 推定の添え字。0件なら出さない（確定と混ぜて見せないため）。 */
export const formatEstimated = (metrics: TAdCodeMetrics): string | null =>
  metrics.estimatedCount > 0
    ? `${adCodeStrings.metrics.estimatedPrefix}${formatCount(
        metrics.estimatedCount,
      )}`
    : null

export const mediumLabel = (medium: TAdCodeMedium): string =>
  mediumOptions.find((option) => option.value === normalizeMedium(medium))
    ?.label ?? medium

export const statusLabel = (status: TAdCodeStatus): string =>
  adCodeStrings.status[status] ?? status

export const confidenceLabel = (confidence: TAdCodeConfidence): string =>
  adCodeStrings.confidence[confidence] ?? confidence

/** 友だちの現在の状態。24時間以内に離脱した人は区別して見せる。 */
export const friendStateLabel = (
  attribution: Pick<TAdCodeAttribution, 'isBlocked' | 'blockedWithin24h'>,
): string => {
  if (attribution.blockedWithin24h) return adCodeStrings.friendState.blocked24h
  if (attribution.isBlocked) return adCodeStrings.friendState.blocked
  return adCodeStrings.friendState.active
}

export const formatDateTime = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const mediumOptions = (
  Object.keys(adCodeStrings.medium) as (keyof typeof adCodeStrings.medium)[]
).map((value) => ({ value, label: adCodeStrings.medium[value] }))

/** 空文字は媒体名として保存できないため、保存済みの名前と衝突しない。 */
export const CUSTOM_MEDIUM = ''
export const AD_CODE_MEDIUM_MAX_LENGTH = 50

export const isPresetMedium = (medium: string): boolean =>
  mediumOptions.some((option) => option.value === medium)

export const normalizeMedium = (medium: string): string => {
  const trimmed = medium.trim()
  const key = trimmed.toLowerCase()
  return (
    mediumOptions.find(
      (option) =>
        option.value.toLowerCase() === key ||
        option.label.toLowerCase() === key,
    )?.value ?? trimmed
  )
}

/** 大文字小文字を区別せず候補をまとめる。選択中の表記は先頭に渡して保持する。 */
export const buildMediumOptions = (
  media: string[],
): Array<{ value: string; label: string }> => {
  const customMedia = new Map<string, string>()
  for (const medium of media) {
    const value = normalizeMedium(medium)
    if (!value || isPresetMedium(value)) continue
    const key = value.toLowerCase()
    if (!customMedia.has(key)) customMedia.set(key, value)
  }
  return [
    ...mediumOptions,
    ...Array.from(customMedia.values())
      .sort((a, b) => a.localeCompare(b, 'ja'))
      .map((value) => ({ value, label: value })),
  ]
}

export const statusOptions = (
  Object.keys(adCodeStrings.status) as TAdCodeStatus[]
).map((value) => ({ value, label: adCodeStrings.status[value] }))
